/**
 * NotesOverviewPage — Story 5.1 (Notizen-System UI, Referenz: notes.html)
 *
 * Globale /notes-Uebersicht als Karten-Liste gemaess Design-Prototyp:
 *  - Typ-Filter-Chips (Alle / Hotspots / Partner / Kontakte (Legacy) / Konto)
 *  - Karten-Zeilen mit Identity-Icon, Origin-Deep-Link (Hotspot -> /hotspots/$id,
 *    Standortgeber/Host -> /hosts/$id), Typ-Pill + Datum
 *  - Inline-Editing (Titel + Notiztext), Loeschen mit Bestaetigung
 *  - Anlegen ueber "+ Neue Notiz" mit notable-Auswahl (CreateNoteModal)
 *  - Erklaerender Empty-State gemaess Vorlage
 *
 * Filter-Mapping (Welle-4b-Review F6 — ehrliche Chips): Der API-Filter matcht
 * genau EINEN origin_type-Rohwert (Contact+Host kombiniert waere serverseitig
 * nicht paginierbar, Contract-Luecke). Deshalb gibt es zwei getrennte Chips:
 * "Partner" filtert auf den v2-Typ Host (Deep-Link /hosts/$id), "Kontakte
 * (Legacy)" auf den Altbestand origin_type=Contact (/contacts/$id -> Redirect
 * /hosts/$id). Frueher hiess der Host-Chip "Kontakte" und verschluckte damit
 * still saemtliche Contact-Notizen.
 *
 * Contract-Luecke Suche: GET /api/v2/notes kennt keinen q-Parameter — die
 * Suche filtert deshalb clientseitig NUR innerhalb der geladenen Seite; das
 * steht als Hinweis (scopeHint) sichtbar ueber der Liste.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { Pencil, Plus, Radio, Search, StickyNote, User as UserIcon, Users, X } from 'lucide-react'
import { useAllNotes, useUpdateNote, useDeleteNote, noteBody, noteLabel, notePath, type Note, type OriginType } from './useNotes'
import { CreateNoteModal } from './CreateNoteModal'
import { DeleteConfirmModal } from '../contacts/DeleteConfirmModal'
import { activeDateFnsLocale } from '../../lib/locale'
import './notes.css'

type OriginFilter = 'ALL' | 'Hotspot' | 'People' | 'User'

type TypeMeta = {
  icon: typeof Radio
  tone: string
  labelKey: string
}

/**
 * Icon/Farb-Ton/Label je origin_type gemaess notes.html
 * (hotspot: radio/tone-1, kontakt: users/tone-2, konto: user/tone-7).
 * Contact (Legacy) und Host (v2-Standortgeber) teilen sich die Kontakt-Optik.
 */
const TYPE_META: Record<string, TypeMeta> = {
  Hotspot: { icon: Radio, tone: 'tone-1', labelKey: 'overview.originType.hotspot' },
  Contact: { icon: Users, tone: 'tone-2', labelKey: 'overview.originType.contact' },
  Host: { icon: Users, tone: 'tone-2', labelKey: 'overview.originType.host' },
  User: { icon: UserIcon, tone: 'tone-7', labelKey: 'overview.originType.user' },
}

const UNKNOWN_META: TypeMeta = { icon: StickyNote, tone: 'tone-8', labelKey: 'overview.originType.unknown' }

function typeMeta(originType: OriginType | undefined): TypeMeta {
  return (originType && TYPE_META[originType]) || UNKNOWN_META
}

function FilterChips({ value, onChange }: { value: OriginFilter, onChange: (v: OriginFilter) => void }) {
  const { t } = useTranslation('notes')
  // Prototyp (notes.html) kennt 4 Chips: All/Hotspots/People/Account. "People"
  // ist ein Client-Merge aus den beiden API-Rohwerten Host (v2-Standortgeber)
  // und Contact (Legacy-Altbestand) — der notes-Endpunkt kann serverseitig nur
  // nach genau einem origin_type filtern, deshalb laedt der People-Chip
  // ungefiltert und filtert clientseitig (siehe visibleNotes unten), analog
  // zur bereits bestehenden Seiten-Scope-Suche.
  const chips: { key: OriginFilter, labelKey: string }[] = [
    { key: 'ALL', labelKey: 'overview.filter.all' },
    { key: 'Hotspot', labelKey: 'overview.filter.hotspots' },
    { key: 'People', labelKey: 'overview.filter.people' },
    { key: 'User', labelKey: 'overview.filter.account' },
  ]
  return (
    <div className="row" role="tablist" aria-label={t('overview.filter.ariaLabel')} data-testid="notes-filter-tabs">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          role="tab"
          aria-selected={value === chip.key}
          className={`chip ${value === chip.key ? 'is-active' : ''}`}
          onClick={() => onChange(chip.key)}
          data-testid={`notes-filter-${chip.key}`}
        >
          {t(chip.labelKey)}
        </button>
      ))}
    </div>
  )
}

type PaginationProps = {
  page: number
  pages: number
  onPageChange: (p: number) => void
}

function Pagination({ page, pages, onPageChange }: PaginationProps) {
  const { t } = useTranslation('notes')
  if (pages <= 1) return null

  return (
    <nav className="pagination" aria-label={t('overview.pagination.navAriaLabel')} data-testid="notes-pagination">
      <button
        className="btn btn-ghost btn-sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label={t('overview.pagination.prevAriaLabel')}
      >
        {t('overview.pagination.prevLabel')}
      </button>
      <span className="pagination-info" aria-live="polite">
        {t('overview.pagination.info', { page, pages })}
      </span>
      <button
        className="btn btn-ghost btn-sm"
        disabled={page >= pages}
        onClick={() => onPageChange(page + 1)}
        aria-label={t('overview.pagination.nextAriaLabel')}
      >
        {t('overview.pagination.nextLabel')}
      </button>
    </nav>
  )
}

/**
 * Erklaerender Empty-State gemaess notes.html (.hint).
 * F7: Bei aktiver Suche wird der Seiten-Scope benannt — „keine Treffer“ heisst
 * hier ausdruecklich „nicht auf dieser Seite“, nicht „nirgends“.
 */
function EmptyState({ filtered, searching, page, pages }: {
  filtered: boolean
  searching: boolean
  page: number
  pages: number
}) {
  const { t } = useTranslation('notes')
  return (
    <div className="hint" data-testid="notes-empty">
      {searching
        ? t('overview.empty.searchHint', { page, pages })
        : filtered
          ? t('overview.empty.filteredHint')
          : t('overview.empty.hint')}
    </div>
  )
}

type NoteRowProps = {
  note: Note
  onDeleteRequest: (note: Note) => void
}

function NoteRow({ note, onDeleteRequest }: NoteRowProps) {
  const { t } = useTranslation('notes')
  const updateNote = useUpdateNote()
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(note.title ?? '')
  const [editText, setEditText] = useState(noteBody(note))

  // F-R5-1: origin_type ist polymorph — fuer unbekannte Typen rendert das FE
  // einen UEBERSETZTEN Fallback, niemals einen technischen Rohwert. Nur bei
  // Typen mit eigener Detailseite (Hotspot/Contact/Host) ist das BE-Label ein
  // echter Objektname und der Deep-Link (notable_path/origin_path) nutzbar.
  const originType = note.origin_type
  const meta = typeMeta(originType)
  const hasOwnLabel = originType === 'Hotspot' || originType === 'Contact' || originType === 'Host'
  const originDisplayLabel = (hasOwnLabel && noteLabel(note)?.trim()) || t(meta.labelKey)
  const originHref = hasOwnLabel ? notePath(note) : null
  const Icon = meta.icon

  function startEdit() {
    setEditTitle(note.title ?? '')
    setEditText(noteBody(note))
    setEditing(true)
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!editText.trim() || note.id == null || note.origin_type == null || note.origin_id == null) return
    updateNote.mutate(
      { id: note.id, origin_type: note.origin_type, origin_id: note.origin_id, title: editTitle, text: editText },
      { onSuccess: () => setEditing(false) }
    )
  }

  return (
    <div className="card card-pad notes-card" data-testid={`notes-row-${note.id}`}>
      <div className="row notes-card-head">
        <span className="pill notes-card-pill">
          <Icon size={11} aria-hidden />
          {t(meta.labelKey)}
        </span>
        {!editing && (
          <div className="row notes-card-icons">
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              title={t('common:actions.edit')}
              onClick={startEdit}
              data-testid={`notes-edit-${note.id}`}
            >
              <Pencil size={12} aria-hidden />
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              title={t('common:actions.delete')}
              style={{ color: 'var(--danger)' }}
              onClick={() => onDeleteRequest(note)}
              data-testid={`notes-delete-${note.id}`}
            >
              <X size={12} aria-hidden />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="notes-edit-form" data-testid={`notes-edit-form-${note.id}`}>
          <input
            type="text"
            className="input"
            placeholder={t('edit.placeholderTitle')}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            disabled={updateNote.isPending}
            aria-label={t('edit.placeholderTitle')}
          />
          <textarea
            className="textarea"
            placeholder={t('edit.placeholderText')}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={updateNote.isPending}
            required
            rows={3}
            aria-label={t('edit.placeholderText')}
          />
          <div className="row notes-edit-actions">
            <button type="button" className="btn btn-sm" onClick={() => setEditing(false)} disabled={updateNote.isPending}>
              {t('common:actions.cancel')}
            </button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={updateNote.isPending || !editText.trim()}>
              {updateNote.isPending ? t('edit.saving') : t('common:actions.save')}
            </button>
          </div>
        </form>
      ) : (
        <div className="notes-card-text">
          {note.title && <div className="notes-row-title">{note.title}</div>}
          {noteBody(note)}
        </div>
      )}

      {!editing && (
        <div className="row notes-card-foot">
          {originHref ? (
            <Link to={originHref} className="notes-origin-link" data-testid={`notes-origin-link-${note.id}`}>
              {originDisplayLabel} →
            </Link>
          ) : (
            <span data-testid={`notes-origin-plain-${note.id}`}>{originDisplayLabel}</span>
          )}
          {note.updated_at && (
            <span className="muted" title={note.updated_at}>
              {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true, locale: activeDateFnsLocale() })}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export function NotesOverviewPage() {
  const { t } = useTranslation('notes')
  const [filter, setFilter] = useState<OriginFilter>('ALL')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [deletingNote, setDeletingNote] = useState<Note | null>(null)

  // "People" hat keinen eigenen origin_type-Rohwert (siehe FilterChips) — der
  // Server-Request laeuft ungefiltert, der Host/Contact-Abgleich passiert
  // unten clientseitig auf der geladenen Seite.
  const { data, isLoading, isError, refetch } = useAllNotes({
    originType: filter === 'ALL' || filter === 'People' ? undefined : filter,
    page,
  })
  const deleteNote = useDeleteNote()

  // Stabile Referenz fuer das useMemo unten (react-hooks/exhaustive-deps).
  const notes = useMemo(() => data?.notes ?? [], [data?.notes])
  const meta = data?.meta

  // Contract-Luecke: kein q-Parameter am notes-Endpunkt und kein kombinierter
  // Host+Contact-Filter — Suche UND der People-Chip filtern daher clientseitig
  // auf der geladenen Seite.
  const visibleNotes = useMemo(() => {
    const q = search.trim().toLowerCase()
    return notes.filter((n) => {
      if (filter === 'People' && n.origin_type !== 'Host' && n.origin_type !== 'Contact') return false
      if (!q) return true
      const haystack = [n.title, noteBody(n), noteLabel(n)].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [notes, search, filter])

  // F7: Suche UND People-Chip greifen nur auf der geladenen Seite — Scope
  // ehrlich benennen.
  const isSearching = search.trim().length > 0
  const isPageScoped = isSearching || filter === 'People'
  const currentPage = meta?.current ?? page
  const totalPages = meta?.pages ?? 1

  function handleFilterChange(next: OriginFilter) {
    setFilter(next)
    setPage(1)
  }

  return (
    <main className="notes-overview-page">
      <header className="section-header">
        <div>
          <h1>{t('overview.title')}</h1>
          <p className="muted">{t('overview.subtitle')}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)} data-testid="notes-create-open">
          <Plus size={14} aria-hidden />
          {t('overview.newNote')}
        </button>
      </header>

      <div className="filterbar notes-filterbar">
        <FilterChips value={filter} onChange={handleFilterChange} />
        <div className="notes-filterbar-spacer" />
        <div className="notes-search">
          <span className="notes-search-icon">
            <Search size={14} aria-hidden />
          </span>
          <input
            className="input search-input notes-search-input"
            type="search"
            placeholder={t('overview.searchPlaceholder')}
            aria-label={t('overview.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="notes-search"
          />
        </div>
      </div>

      <section aria-label={t('overview.listAriaLabel')}>
        {isLoading && (
          <div className="notes-grid" aria-hidden data-testid="notes-loading">
            <div className="card card-pad notes-card"><div className="skeleton" style={{ height: 96 }} /></div>
            <div className="card card-pad notes-card"><div className="skeleton" style={{ height: 96 }} /></div>
            <div className="card card-pad notes-card"><div className="skeleton" style={{ height: 96 }} /></div>
          </div>
        )}

        {isError && !isLoading && (
          <div className="error-state" role="alert">
            <p>{t('overview.error.loadNotes')}</p>
            <button className="btn btn-primary" onClick={() => refetch()}>
              {t('common:actions.retry')}
            </button>
          </div>
        )}

        {!isLoading && !isError && isPageScoped && visibleNotes.length > 0 && (
          <div className="hint" role="status" data-testid="notes-search-scope">
            {t('overview.searchScopeHint', { page: currentPage, pages: totalPages })}
          </div>
        )}

        {!isLoading && !isError && visibleNotes.length === 0 && (
          <EmptyState
            filtered={filter !== 'ALL' || isSearching}
            searching={isSearching}
            page={currentPage}
            pages={totalPages}
          />
        )}

        {!isLoading && !isError && visibleNotes.length > 0 && (
          <div className="notes-grid" data-testid="notes-list">
            {visibleNotes.map((note) => (
              <NoteRow key={note.id} note={note} onDeleteRequest={setDeletingNote} />
            ))}
          </div>
        )}
      </section>

      {meta && <Pagination page={meta.current ?? page} pages={meta.pages ?? 1} onPageChange={setPage} />}

      <CreateNoteModal open={creating} onClose={() => setCreating(false)} />

      <DeleteConfirmModal
        title={t('delete.modalTitle')}
        message={t('delete.modalMessage')}
        isOpen={deletingNote !== null}
        isPending={deleteNote.isPending}
        onConfirm={() => {
          if (deletingNote?.id != null && deletingNote.origin_type != null && deletingNote.origin_id != null) {
            deleteNote.mutate(
              { id: deletingNote.id, origin_type: deletingNote.origin_type, origin_id: deletingNote.origin_id },
              { onSuccess: () => setDeletingNote(null) }
            )
          }
        }}
        onClose={() => setDeletingNote(null)}
      />
    </main>
  )
}
